/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useEffect,
  createContext,
  useContext
} from 'react';
import PropTypes from 'prop-types';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import { TextField, Autocomplete } from '@mui/material';
import { FixedSizeList } from 'react-window';
import {
  ANALYTICS_CONSTS,
  AnalyticsUtils,
  API_PATHS,
  apiClient,
  GdprUtils,
  trackGoogleAnalyticsEvent,
  UserUtils
} from '@cyberint/common-utils';
import { CopyableTitle, CustomTooltip, Icons, IconSizes } from '@cyberint/dsc';
import { UserDisplay } from '@cyberint/stateful-ui';
import { t } from '../../i18n';
import * as S from './styled';

const SX = { minWidth: 40, maxWidth: 250 };
const emptyArr = [];

const { defaultClient } = apiClient;
const { API_V1 } = API_PATHS;

// Returns a single-use handoff URL to open on the target customer's server (leaving this session
// untouched), or null when the backend swapped the cookie in place instead -> caller should reload.
const startImpersonation = async customerId => {
  const { data } = await defaultClient.post(`${API_V1}/impersonate`, { customer_id: customerId });
  return data?.response?.handoff_url ?? null;
};

// Dev-proxy hostnames are `<tenant>[.env].cyberint.localhost` (see apps/proxy-tenant-router.js)
// and mirror the real `<tenant>[.env].cyberint.io` host the backend hands off to. The router
// keys off each request's own Host header, not the app's origin, so this fires whenever the app
// is served by the dev server at all -- plain `localhost` included -- not just when it's already
// on a tenant sub-host. Rewriting the handoff URL onto the matching dev host keeps the
// impersonation tab on the local dev server -- proxied through it, like every other request --
// instead of jumping straight to the real tenant and leaving dev entirely.
const DEV_HOST = /^(?:localhost|[a-z0-9-]+(?:\.[a-z0-9-]+)*\.cyberint\.localhost)$/;
const REAL_TENANT_HOST = /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.cyberint\.io$/;

export const toDevProxyHandoffUrl = (handoffUrl, location = window.location) => {
  if (!DEV_HOST.test(location.hostname.toLowerCase())) {
    return handoffUrl;
  }

  const url = new URL(handoffUrl);
  if (!REAL_TENANT_HOST.test(url.hostname.toLowerCase())) {
    return handoffUrl;
  }

  url.protocol = location.protocol;
  url.hostname = url.hostname.replace(/\.cyberint\.io$/i, '.cyberint.localhost');
  url.port = location.port;
  return url.toString();
};

// Marquee speed for a hovered, truncated tenant name -- constant px/s rather than a fixed
// duration, so a barely-truncated name doesn't crawl and a very long one doesn't fly by too
// fast to read. Measured lazily on hover (not on every render) since it needs real layout sizes.
const MARQUEE_PX_PER_SEC = 60;
const MARQUEE_MIN_DURATION = 0.6;
const MARQUEE_MAX_DURATION = 4;

const handleMarqueeHover = e => {
  const viewport = e.currentTarget;
  const textEl = viewport.firstElementChild;
  const distance = textEl.scrollWidth - viewport.clientWidth;
  if (distance <= 0) {
    viewport.style.removeProperty('--marquee-distance');
    viewport.style.removeProperty('--marquee-duration');
    return;
  }
  const duration = Math.min(MARQUEE_MAX_DURATION, Math.max(MARQUEE_MIN_DURATION, distance / MARQUEE_PX_PER_SEC));
  viewport.style.setProperty('--marquee-distance', `${distance}px`);
  viewport.style.setProperty('--marquee-duration', `${duration}s`);
};

// Virtualized listbox — with hundreds of tenants, MUI's default Autocomplete listbox
// mounts a DOM node per option, which makes opening/scrolling the dropdown janky.
// react-window keeps only the visible rows mounted regardless of tenant count.
const LISTBOX_PADDING = 8;
const FALLBACK_ROW_HEIGHT = 40;
const VISIBLE_ROWS = 8;

const OuterElementContext = createContext({});

// MUI's Autocomplete attaches onMouseDown={e => e.preventDefault()} to the listbox root to stop
// the input from blurring when an option is clicked. That handler doesn't check where the click
// landed, and calling preventDefault() on mousedown is enough in most browsers to block a native
// scrollbar thumb's drag gesture -- invisible with the old short, unscrolled listbox, but very
// visible now that a long tenant list actually needs to be dragged. Skip it for clicks that land
// in the scrollbar's own gutter.
const isScrollbarClick = (container, clientX) => {
  const scrollbarWidth = container.offsetWidth - container.clientWidth;
  return scrollbarWidth > 0 && clientX >= container.getBoundingClientRect().right - scrollbarWidth;
};

// react-window sets overflow:'auto' on both axes on this scroll container; this list only ever
// needs to scroll vertically, and a few px of sub-pixel rounding between its computed width and
// the scrollbar it reserves space for was enough to trigger a spurious horizontal scrollbar.
const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  const { style, ...restProps } = props;
  const { onMouseDown, ...restOuterProps } = outerProps;

  const handleMouseDown = event => {
    if (onMouseDown && !isScrollbarClick(event.currentTarget, event.clientX)) onMouseDown(event);
  };

  return (
    <div
      ref={ref}
      {...restProps}
      {...restOuterProps}
      onMouseDown={handleMouseDown}
      style={{ ...style, overflowX: 'hidden' }}
    />
  );
});

// Only forward the positioning properties react-window needs. MUI's own .MuiAutocomplete-option
// CSS relies on width:auto to fit the listbox minus its own padding (calc(100% - 5px) in our
// styles); forwarding react-window's width:'100%' here overrides that and made rows overflow
// the listbox horizontally.
const renderRow = ({ data, index, style }) =>
  React.cloneElement(data[index], {
    style: { position: style.position, left: style.left, top: style.top + LISTBOX_PADDING, height: style.height }
  });

const VirtualizedListbox = React.forwardRef((props, ref) => {
  // eslint-disable-next-line no-unused-vars
  const { children, ownerState, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;

  // Measured off the first real rendered option rather than hardcoded or a detached probe —
  // row height depends on MUI's .MuiAutocomplete-option padding, which only applies once the
  // option is actually mounted inside the listbox. Can't read the option's own offsetHeight
  // though: renderRow already forces an inline height (=rowHeight) onto that same element, so
  // measuring it back just echoes rowHeight regardless of what the content actually needs.
  // Measure its first (unconstrained) child instead and add the option's own real padding.
  const [rowHeight, setRowHeight] = useState(FALLBACK_ROW_HEIGHT);
  const containerRef = useRef(null);
  const setContainerRef = node => {
    containerRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };
  useLayoutEffect(() => {
    const optionEl = containerRef.current?.querySelector('.MuiAutocomplete-option');
    const contentEl = optionEl?.firstElementChild;
    if (!optionEl || !contentEl) return;
    const { paddingTop, paddingBottom } = window.getComputedStyle(optionEl);
    const measured = Math.ceil(
      contentEl.offsetHeight + (parseFloat(paddingTop) || 0) + (parseFloat(paddingBottom) || 0)
    );
    if (measured && measured !== rowHeight) setRowHeight(measured);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCount]);

  const height = Math.min(VISIBLE_ROWS, itemCount || 1) * rowHeight + 2 * LISTBOX_PADDING;

  // The default MUI listbox scrolls the selected option into view on open; react-window
  // otherwise always mounts at offset 0, so a selected tenant further down the list would
  // open off-screen. Recenter on it here instead.
  const selectedIndex = itemData.findIndex(item => item?.props?.selected);
  const initialScrollOffset = selectedIndex > 0 ? Math.max(0, selectedIndex * rowHeight - (height - rowHeight) / 2) : 0;

  // initialScrollOffset only applies on mount, using whatever rowHeight was at that point (the
  // fallback guess, since the measurement effect above hasn't corrected it yet). Once it does
  // correct, itemSize changes but the already-applied scroll offset doesn't move with it, so the
  // list ends up centered on the wrong row. Resync with scrollToItem whenever rowHeight settles.
  const listRef = useRef(null);
  useLayoutEffect(() => {
    if (selectedIndex > 0) listRef.current?.scrollToItem(selectedIndex, 'center');
  }, [rowHeight, selectedIndex]);

  return (
    <div ref={setContainerRef}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          ref={listRef}
          height={height}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={rowHeight}
          overscanCount={5}
          itemCount={itemCount}
          itemData={itemData}
          initialScrollOffset={initialScrollOffset}>
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

VirtualizedListbox.propTypes = {
  children: PropTypes.node
};

const getTenantName = tenant => tenant?.name || tenant?._id;

const filterTenants = (options, { inputValue }) => {
  const query = inputValue.trim().toLowerCase();
  if (!query) return options;
  const nameMatches = [];
  const idMatches = [];
  options.forEach(tenant => {
    if ((tenant?.name || '').toLowerCase().includes(query)) {
      nameMatches.push(tenant);
    } else if ((tenant?._id || '').toLowerCase().includes(query)) {
      idMatches.push(tenant);
    }
  });
  return [...nameMatches, ...idMatches];
};

const renderInput = params => <TextField variant="standard" {...params} />;

const TooltipItem = ({ title, value }) => (
  <div>
    <span>{title}:</span>
    <span>{value}</span>
  </div>
);

TooltipItem.propTypes = {
  title: PropTypes.string,
  value: PropTypes.node
};

// onMouseDown guard keeps the click from blurring the Autocomplete and closing the dropdown.
const CopyTenantId = ({ value }) => (
  <S.CopyableId
    onMouseDown={e => {
      e.stopPropagation();
      e.preventDefault();
    }}>
    <CopyableTitle title={value} valueToCopy={value} />
  </S.CopyableId>
);

CopyTenantId.propTypes = {
  value: PropTypes.string
};

/**
 * Per-tenant "View as" control rendered inside the option, right before the info tooltip.
 * Eligibility is decided by the wrapper (Cyberint root + FeatureHub flag + not already
 * impersonating) and gates whether this renders; the impersonation action itself is injected as
 * `onImpersonate`, since the API call / analytics / reload are app concerns that can't live in a
 * shared lib. On success the app reloads, so the spinner only resets on failure.
 */
const ImpersonateTenantButton = ({ customer, selected, onImpersonate }) => {
  const [isStarting, setIsStarting] = useState(false);

  const handleClick = async e => {
    e.stopPropagation(); // don't let the click select/close the tenant option
    setIsStarting(true);
    try {
      await onImpersonate(customer);
    } catch (err) {
      setIsStarting(false);
    }
  };

  return (
    <CustomTooltip title={t('impersonation.viewAs', { customerName: getTenantName(customer) })}>
      <S.ImpersonateIcon
        $selected={selected}
        onClick={isStarting ? undefined : handleClick}
        onMouseDown={e => {
          e.stopPropagation();
          e.preventDefault();
        }}
        data-testid="btn-impersonate-customer">
        {isStarting ? <Icons.InProgress size={14} rotate /> : <Icons.Visibility size={14} />}
      </S.ImpersonateIcon>
    </CustomTooltip>
  );
};

ImpersonateTenantButton.propTypes = {
  customer: PropTypes.object,
  selected: PropTypes.bool,
  onImpersonate: PropTypes.func
};

const TenantOption = ({
  customer,
  selected,
  breadCrumbMode,
  canImpersonate,
  canViewTenantId,
  onImpersonate,
  ...props
}) => {
  const name = getTenantName(customer);
  const { parents, createdBy, createdDate } = customer._meta || {};
  const tenantType = customer.tenant_type ? t(`customers.tenant_types.${customer.tenant_type}.title`) : null;

  const Tenant = breadCrumbMode ? S.TenantOption : S.NavigationTenantOption;
  const tooltipContent = (
    <S.TooltipContentWrapper>
      {canViewTenantId && customer._id && (
        <TooltipItem title={t('customers.customer_id')} value={<CopyTenantId value={customer._id} />} />
      )}
      {parents && <TooltipItem title={t('customers.parent')} value={parents} />}
      {tenantType && <TooltipItem title={t('customers.tenant_type')} value={tenantType} />}
      {createdBy && (
        <TooltipItem title={t('common.created_by')} value={<UserDisplay userId={createdBy} format="username" />} />
      )}
      {createdDate && <TooltipItem title={t('common.created_date')} value={createdDate} />}
    </S.TooltipContentWrapper>
  );

  return (
    <Tenant $selected={selected} {...props} title={name} key={customer._id}>
      <S.OptionText onMouseEnter={handleMarqueeHover}>
        <span>{name}</span>
      </S.OptionText>
      <S.TenantOptionActions>
        {canImpersonate && (
          <ImpersonateTenantButton customer={customer} selected={selected} onImpersonate={onImpersonate} />
        )}
        <CustomTooltip title={tooltipContent} placement="right" width={380} maxWidth={420}>
          <S.TenantInfoIcon $selected={selected}>
            <Icons.Info size={14} />
          </S.TenantInfoIcon>
        </CustomTooltip>
      </S.TenantOptionActions>
    </Tenant>
  );
};

TenantOption.propTypes = {
  customer: PropTypes.object,
  selected: PropTypes.bool,
  breadCrumbMode: PropTypes.bool,
  canImpersonate: PropTypes.bool,
  canViewTenantId: PropTypes.bool,
  onImpersonate: PropTypes.func
};

export const TenantSelectorBase = ({
  breadCrumbMode,
  testId,
  user,
  currentCustomer,
  setCurrentCustomer,
  tenants = emptyArr,
  isLoading,
  region,
  onImpersonateError
}) => {
  const [sx, setSx] = useState(null);

  const regionBasedTenants = useMemo(
    () => tenants.filter(ten => GdprUtils.normalizeRegion(ten.region) === GdprUtils.normalizeRegion(region)),
    [tenants, region]
  );
  const tenant = regionBasedTenants.find(c => c._id === currentCustomer) || {};
  const ownCustomerId = user?.customer?._id;
  const canViewTenantId = !!user && (UserUtils.isCyberintRootUser(user) || UserUtils.isCyberintMgmtAdmin(user));

  // Only a Cyberint root user who isn't already impersonating may impersonate a tenant.
  const canImpersonate = !!user && !user.impersonating && UserUtils.isCyberintRootUser(user);

  const onImpersonate = useCallback(
    async customer => {
      trackGoogleAnalyticsEvent({
        [ANALYTICS_CONSTS.GA_CATEGORY]: ANALYTICS_CONSTS.GA_CATEGORY_SYSTEM,
        [ANALYTICS_CONSTS.GA_ACTION]: 'start customer impersonation',
        [ANALYTICS_CONSTS.META_DATA]: AnalyticsUtils.buildFeatureUsageMetadata({
          event_name: 'feature_interaction',
          feature_area: 'impersonation',
          feature_name: 'customer_impersonation',
          feature_action: 'start_impersonation',
          screen_id: 'tenant_selector',
          source_surface: 'tenant_selector_list'
        })
      });
      // Open the tab synchronously inside the click handler so the browser's popup blocker allows it
      // (it can't survive the await otherwise); the handoff URL is filled in once the API responds.
      const impersonationTab = window.open('', '_blank');
      try {
        const handoffUrl = await startImpersonation(customer._id);
        if (handoffUrl) {
          const devAwareHandoffUrl = toDevProxyHandoffUrl(handoffUrl);
          // Enter the impersonation session in the new tab, on the customer's server.
          if (impersonationTab) {
            impersonationTab.location.href = devAwareHandoffUrl;
          } else {
            window.location.href = devAwareHandoffUrl; // popup blocked: fall back to navigating the current tab
          }
        } else {
          // Cookie was swapped in place; discard the unused tab and reload to pick up the new session.
          if (impersonationTab) {
            impersonationTab.close();
          }
          window.location.reload();
        }
      } catch (err) {
        if (impersonationTab) {
          impersonationTab.close();
        }
        // Error presentation depends on the host app having a SnackbarProvider, so it's injected.
        onImpersonateError?.(err);
        throw err; // re-throw so the button resets its spinner
      }
    },
    [onImpersonateError]
  );

  useEffect(() => {
    if (regionBasedTenants.length > 0 && !regionBasedTenants.some(ten => ten._id === currentCustomer)) {
      setCurrentCustomer(regionBasedTenants[0]._id);
    }
  }, [region, tenants]);

  const controlRef = useRef();
  const onSelect = useCallback((e, selectedCustomer) => {
    if (!selectedCustomer) return;
    setCurrentCustomer(selectedCustomer._id);
    /** Similar to mssp-list.js
     * cleanupSessionStorage
     * Need also to handle if (!manualChange && showNotification) - NOT HERE IN THIS COMPONENT
     * */
  }, []);

  const renderOption = useCallback(
    (props, option, state) => (
      <TenantOption
        customer={option}
        selected={state.selected}
        breadCrumbMode={breadCrumbMode}
        canImpersonate={canImpersonate && option._id !== ownCustomerId}
        canViewTenantId={canViewTenantId}
        onImpersonate={onImpersonate}
        {...props}
        key={option._id}
      />
    ),
    [breadCrumbMode, canImpersonate, canViewTenantId, ownCustomerId, onImpersonate]
  );

  const tenantName = getTenantName(tenant) || getTenantName(user?.customer) || t('common.invalid-customer');
  useLayoutEffect(() => {
    const offWidth = controlRef?.current?.offsetWidth;
    breadCrumbMode && offWidth && setSx({ ...SX, width: offWidth + 25 }); // taking into account paddings/margins
  }, [tenantName]);

  const Wrapper = breadCrumbMode ? S.TenantBreadcrumbWrapper : S.TenantsWrapper;

  return (
    <Wrapper $isLoading={isLoading}>
      {/** Pseudo-element (outside of display) help calculate the width of the selected tenant even if changed programmatically */}
      <S.TenantName ref={controlRef} $hidden>
        {tenantName}
      </S.TenantName>
      {!tenants || tenants.length <= 1 ? (
        <S.TenantName>{tenantName}</S.TenantName>
      ) : (
        <Autocomplete
          blurOnSelect
          value={tenant}
          data-testid={testId}
          id="combo-box-demo"
          disablePortal
          disableClearable
          onChange={onSelect}
          options={regionBasedTenants}
          title={tenantName}
          sx={sx}
          getOptionLabel={getTenantName}
          filterOptions={canViewTenantId ? filterTenants : undefined}
          // getOptionDisabled={c => c?._id === customer?._id}
          renderInput={renderInput}
          renderOption={renderOption}
          ListboxComponent={VirtualizedListbox}
          popupIcon={<KeyboardArrowDownRounded />}
        />
      )}
      {isLoading && <Icons.InProgress className="tenants-loading" size={IconSizes.S} rotate />}
    </Wrapper>
  );
};

TenantSelectorBase.propTypes = {
  breadCrumbMode: PropTypes.bool,
  testId: PropTypes.string,
  user: PropTypes.object,
  currentCustomer: PropTypes.string,
  setCurrentCustomer: PropTypes.func.isRequired,
  tenants: PropTypes.array,
  isLoading: PropTypes.bool,
  region: PropTypes.string,
  onImpersonateError: PropTypes.func
};
